import { db } from '@/services/firebase'
import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  onSnapshot,
  where
} from 'firebase/firestore'

const state = {
  orders: [],
  loading: false,
  error: null,
  currentOrder: null
}

const getters = {
  allOrders: state => state.orders,
  pendingOrders: state => state.orders.filter(order => order.status === 'pending'),
  processingOrders: state => state.orders.filter(order => order.status === 'processing'),
  shippedOrders: state => state.orders.filter(order => order.status === 'shipped'),
  completedOrders: state => state.orders.filter(order => order.status === 'completed'),
  cancelledOrders: state => state.orders.filter(order => order.status === 'cancelled'),
  pickupOrders: state => state.orders.filter(order => order.deliveryMethod === 'pickup'),
  deliveryOrders: state => state.orders.filter(order => order.deliveryMethod === 'delivery'),
  orderById: state => id => state.orders.find(order => order.id === id),
  isLoading: state => state.loading,
  error: state => state.error
}

const actions = {
  async fetchOrders({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const ordersQuery = query(collection(db, 'orders'), orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(ordersQuery)
      
      const orders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Ensure total is a number for sorting/filtering
        total: doc.data().total || 0
      }))
      
      commit('SET_ORDERS', orders)
      commit('SET_LOADING', false)
      
      return orders
    } catch (error) {
      console.error('Error fetching orders:', error)
      commit('SET_ERROR', 'Failed to load orders. Please try again.')
      commit('SET_LOADING', false)
      throw error
    }
  },
  
  async fetchOrdersByUser({ commit }, userId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const ordersQuery = query(
        collection(db, 'orders'), 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      )
      
      const snapshot = await getDocs(ordersQuery)
      
      const orders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        total: doc.data().total || 0
      }))
      
      commit('SET_ORDERS', orders)
      commit('SET_LOADING', false)
      
      return orders
    } catch (error) {
      console.error('Error fetching user orders:', error)
      commit('SET_ERROR', 'Failed to load your orders. Please try again.')
      commit('SET_LOADING', false)
      throw error
    }
  },
  
  async fetchOrderById({ commit }, orderId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      
      const orderRef = doc(db, 'orders', orderId)
      const orderDoc = await getDocs(orderRef)
      
      if (orderDoc.exists()) {
        const order = {
          id: orderDoc.id,
          ...orderDoc.data()
        }
        
        commit('SET_CURRENT_ORDER', order)
        commit('SET_LOADING', false)
        
        return order
      } else {
        throw new Error('Order not found')
      }
    } catch (error) {
      console.error('Error fetching order:', error)
      commit('SET_ERROR', 'Failed to load order details. Please try again.')
      commit('SET_LOADING', false)
      throw error
    }
  },
  
  async updateOrderStatus({ commit }, { orderId, status }) {
    try {
      const orderRef = doc(db, 'orders', orderId)
      await updateDoc(orderRef, { status })
      
      // Update local state
      commit('UPDATE_ORDER_STATUS', { orderId, status })
      
      return { success: true }
    } catch (error) {
      console.error('Error updating order status:', error)
      throw error
    }
  }
}

const mutations = {
  SET_ORDERS(state, orders) {
    state.orders = orders
  },
  
  SET_CURRENT_ORDER(state, order) {
    state.currentOrder = order
  },
  
  SET_LOADING(state, status) {
    state.loading = status
  },
  
  SET_ERROR(state, error) {
    state.error = error
  },
  
  UPDATE_ORDER_STATUS(state, { orderId, status }) {
    const order = state.orders.find(o => o.id === orderId)
    if (order) {
      order.status = status
    }
    
    if (state.currentOrder && state.currentOrder.id === orderId) {
      state.currentOrder.status = status
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 